import { useEffect, useState, useRef } from 'react';
import IconPlay from '@hawkins/assets/lib/icons/iconPlay';
import IconPause from '@hawkins/assets/lib/icons/iconPause';

type PlayerState = {
  duration?: number | undefined;
  currentTime?: number | undefined;
  remainingTime?: number | undefined;
  percent?: number | undefined;
}

export const AudioPlayer = ({
  src,
  title,
  img
}: {
  src: string;
  title: string;
  img: string;
}) => {

  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [playerState, setPlayerState] = useState<PlayerState>({
    duration: 0,
    currentTime: 0,
    remainingTime: 0,
    percent: 0
  });

  const player = useRef<HTMLAudioElement>(null);

  const updatePlayerState = (newVals: PlayerState) => {
    setPlayerState((prevVals: PlayerState) => {
      return ({
        ...prevVals,
        ...newVals
      })
    });
  }

  const togglePlay = () => {
    if (isPlaying) {
      player?.current?.pause();
      setIsPlaying(false);

    } else {
      player?.current?.play();
      setIsPlaying(true);
    }
  }

  const stop = () => {
    if (player?.current) {
      player.current.pause();
      player.current.currentTime = 0;
    }

    setIsPlaying(false);
  }

  const handleCanPlay = () => {
    setIsLoading(false);
  }

  const handleDurationChange = () => {
    const playerDuration = Math.round(getDuration());

    updatePlayerState({
      duration: playerDuration
    });
  }

  const handleTimeUpdate = () => {
    if (!player?.current) {
      return;
    }

    updatePlayerState({
      currentTime: player?.current?.currentTime
    });

    updateProgress();
  }

  const handleEnded = () => {
    stop();
  }

  const getDuration = (): number => {
    if (isNaN(player?.current?.duration as number)) {
      return 0;
    }

    return player?.current?.duration as number;
  }

  const updateProgress = () => {
    const progress = player?.current?.currentTime || 0;
    const remainingTime = getDuration() - progress;

    let percent = (progress / getDuration()) * 100;
    if (isNaN(percent)) {
      percent = 0;
    }

    updatePlayerState({
      remainingTime: remainingTime,
      percent: percent
    });
  }

  const formatTime = (seconds: number) => {
    seconds = Math.round(seconds);

    if (isNaN(seconds)) {
      return '';
    }

    const time = [];
    const hours = Math.floor(seconds / 60 / 60);
    const minutes = Math.floor((seconds / 60) % 60);
    seconds = Math.floor(seconds % 60);

    if (hours) {
      time.push(hours);
    }

    if (hours && minutes < 10) {
      time.push('0'+ minutes);

    } else {
      time.push(minutes);
    }

    if (seconds < 10) {
      time.push('0'+ seconds);

    } else {
      time.push(seconds);
    }

    return time.join(':');
  }

  useEffect(() => {
    if (src) {
      player?.current?.addEventListener('canplay', handleCanPlay);
      player?.current?.addEventListener('durationchange', handleDurationChange);
      player?.current?.addEventListener('timeupdate', handleTimeUpdate);
      player?.current?.addEventListener('ended', handleEnded);
    }

    const cleanUpRef = player?.current;

    return () => {
      if (cleanUpRef) {
        cleanUpRef.removeEventListener('canplay', handleCanPlay);
        cleanUpRef.removeEventListener('durationchange', handleDurationChange);
        cleanUpRef.removeEventListener('timeupdate', handleTimeUpdate);
        cleanUpRef.removeEventListener('ended', handleEnded);
      }
    }
  // eslint-disable-next-line
  }, [src]);

  return (
    <div
      className="flex flex-col dsk:flex-row items-center gap-[20px] dsk:gap-[75px] border border-brand-black py-[30px] pl-[30px] pr-[30px] dsk:py-[110px] dsk:pl-[110px] dsk:pr-[70px] rounded-xl"
    >
      {img &&
        <div
          className="w-[230px] h-[230px] rounded-xl shrink-0 bg-no-repeat bg-cover bg-center"
          style={{ backgroundImage: `url(${img})` }}
          role="img"
          aria-label={title}
        />
      }
      <div className="w-full">
        {title &&
          <h3 className="text-heading-3 mb-8">{title}</h3>
        }

        {src &&
          <div className="audio-player">
            <div className="time flex items-center justify-between mb-[10px]">
              <div>{formatTime(playerState.currentTime as number)}</div>
              <div>{formatTime(playerState.duration as number)}</div>
            </div>
            <div className="controls flex items-center gap-[10px]">
              {isLoading ?
                <>...</>
              :
                <button
                  onClick={togglePlay}
                >
                  {isPlaying ? <IconPause /> : <IconPlay />}
                </button>
              }

              <div className="track w-full h-[4px] bg-brand-black/30">
                <div className="progress h-[4px] bg-brand-black" style={{ width: `${playerState.percent}%` }} />
              </div>
            </div>
            <audio
              ref={player}
              src={src as string}
            />
          </div>
        }
      </div>
    </div>
  );
};

export default AudioPlayer;